import { CheckCircle2, ChevronDown, ClipboardCheck, Plus, Search } from "lucide-react";
import { can, type SessionUser } from "../../shared/auth/access";
import { capaSourceLabels, capaStatusLabels } from "./domain/models";
import { useQualityCapaWorkspace } from "./useQualityCapaWorkspace";
import { stages, date } from "./capa-presentation";
import { CapaForm } from "./CapaForm";

type Props = {
  user: SessionUser;
  notify: (message: string) => void;
  dataRefreshVersion: number;
};

export function QualityCapaModule({ user, notify, dataRefreshVersion }: Props) {
  const { records, loading, search, setSearch, stage, setStage, expanded, setExpanded, formKind, selected, form, formErrors, saving, visible, open, field, close, submit } = useQualityCapaWorkspace({user,notify,dataRefreshVersion});

  return (
    <>
      <div className="module-header">
        <div>
          <span className="section-kicker">Kalite yönetimi</span>
          <h1>DÖF Yönetimi</h1>
          <p>
            Uygunsuzlukları kök nedeninden etkinlik kontrolüne kadar izleyin.
          </p>
        </div>
        {can(user, "dof.create") ? (
          <button className="primary-button" onClick={() => open("create")}>
            <Plus size={16} /> Yeni DÖF
          </button>
        ) : null}
      </div>
      <section className="capa-status-filter" aria-label="DÖF durumları">
        {stages.map((option) => (
          <button
            key={option.id}
            className={stage === option.id ? "is-active" : ""}
            onClick={() => setStage(option.id)}
          >
            <span>{option.label}</span>
            <strong>
              {records.filter((record) => record.status === option.id).length}
            </strong>
          </button>
        ))}
      </section>
      <section className="module-card capa-workspace">
        <div className="capa-toolbar">
          <div className="request-search">
            <Search size={15} />
            <input
              aria-label="DÖF ara"
              placeholder="DÖF numarası, konu veya bildiren kişi ara"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          {stage ? (
            <button className="text-button" onClick={() => setStage(null)}>
              Filtreyi temizle
            </button>
          ) : null}
        </div>
        {loading ? (
          <Empty title="DÖF kayıtları hazırlanıyor" />
        ) : visible.length ? (
          <div className="capa-list">
            {visible.map((record) => (
              <article
                key={record.id}
                className={`capa-row severity-${record.severity.toLowerCase()}`}
              >
                <button
                  className="capa-row-main"
                  aria-expanded={expanded === record.id}
                  onClick={() =>
                    setExpanded(expanded === record.id ? null : record.id)
                  }
                >
                  <div>
                    <span className="capa-number">{record.capaNo}</span>
                    <strong>{record.title}</strong>
                    <small>
                      {capaSourceLabels[record.sourceType]} ·{" "}
                      {record.createdByName}
                    </small>
                  </div>
                  <div className="capa-row-meta">
                    <span
                      className={`capa-state state-${record.status.toLowerCase()}`}
                    >
                      {capaStatusLabels[record.status]}
                    </span>
                    <small>
                      {record.actionOwnerName
                        ? `Sorumlu: ${record.actionOwnerName}`
                        : "Kalite ataması bekleniyor"}
                    </small>
                  </div>
                  <div className="capa-row-meta">
                    <small>Termin</small>
                    <strong>{date(record.dueAt)}</strong>
                  </div>
                  <ChevronDown size={17} />
                </button>
                {expanded === record.id ? (
                  <div className="capa-detail">
                    <div className="capa-detail-grid">
                      <Detail label="Uygunsuzluk" value={record.description} />
                      <Detail label="Kök neden" value={record.rootCause} />
                      <Detail
                        label="Faaliyet planı"
                        value={record.actionPlan}
                      />
                      <Detail
                        label="Referanslar"
                        value={[
                          record.supplierReference,
                          record.purchaseOrderReference,
                          record.materialReference,
                          record.lotReference,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      />
                    </div>
                    <div className="capa-actions">
                      {record.status === "UNDER_REVIEW" &&
                      can(user, "dof.evaluate") ? (
                        <button
                          className="primary-button"
                          onClick={() => open("evaluate", record)}
                        >
                          Değerlendir ve ata
                        </button>
                      ) : null}
                      {record.status === "ACTION_IN_PROGRESS" &&
                      can(user, "dof.complete.action") ? (
                        <button
                          className="primary-button"
                          onClick={() => open("complete", record)}
                        >
                          Faaliyeti tamamla
                        </button>
                      ) : null}
                      {record.status === "EFFECTIVENESS_REVIEW" &&
                      can(user, "dof.verify.effectiveness") ? (
                        <button
                          className="primary-button"
                          onClick={() => open("verify", record)}
                        >
                          Etkinliği doğrula
                        </button>
                      ) : null}
                    </div>
                    {record.history.length ? (
                      <div className="capa-history">
                        {record.history.map((entry) => (
                          <div key={entry.id}>
                            <CheckCircle2 size={14} />
                            <span>
                              <strong>{entry.action}</strong>
                              <small>
                                {entry.actorName} · {entry.actorRole} ·{" "}
                                {date(entry.occurredAt)}
                              </small>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <Empty
            title="Bu görünümde DÖF kaydı yok"
            description="Filtreyi değiştirin veya yeni bir uygunsuzluk bildirin."
          />
        )}
      </section>
      {formKind ? (
        <CapaForm
          kind={formKind}
          selected={selected}
          form={form}
          errors={formErrors}
          field={field}
          saving={saving}
          close={close}
          submit={() => void submit()}
        />
      ) : null}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <small>{label}</small>
      <p>{value || "Henüz bilgi girilmedi."}</p>
    </div>
  );
}
function Empty({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="module-empty">
      <ClipboardCheck size={28} />
      <strong>{title}</strong>
      {description ? <p>{description}</p> : null}
    </div>
  );
}
